import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { isNumeric } from 'utils/string';

const TableEditor = React.forwardRef(({ data, columns, onDataChange }, ref) => {
  const tableRef = useRef(null);
  const tabulatorRef = useRef(null);
  const [localData, setLocalData] = useState(data);
  console.log(data);
  // Update local data when parent data changes
  useEffect(() => {
    setLocalData(data);
    if (tabulatorRef.current) {
      tabulatorRef.current.setData(data);
    }
  }, [data]);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/tabulator-tables@5.5.2/dist/css/tabulator.min.css';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      .tabulator-container {
        height: 100%;
      }
      .tabulator {
        height: 100%;
        max-height: unset !important;
        font-size: 14px;
      }
      .tabulator .tabulator-tableholder {
        overflow-y: auto;
        overflow-x: auto;
      }
      .tabulator .tabulator-header {
        background-color: #f5f5f5;
      }
      .tabulator .tabulator-header .tabulator-col {
        height: 80px !important;
        background-color: #f5f5f5;
        border-right: 1px solid #ddd;
      }
      .tabulator .tabulator-header .tabulator-col .tabulator-col-content {
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
      }
      .tabulator .tabulator-header .tabulator-col .tabulator-col-title {
        white-space: pre-wrap;
        line-height: 1.2;
        text-align: center;
        font-weight: 500;
        font-size: 12px;
        word-break: break-word;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
      }
      .tabulator-row .tabulator-cell {
        padding: 8px;
        border-right: 1px solid #eee;
        text-align: center;
      }
      .tabulator-row .tabulator-cell input {
        text-align: center;
      }
      .tabulator input:focus {
        outline: 2px solid #1890ff;
        border-radius: 2px;
      }
      .tabulator-row.tabulator-selected {
        background-color: #e6f7ff !important;
      }
      .tabulator-row.tabulator-selected:hover {
        background-color: #bae7ff !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    
    if (!tabulatorRef.current) return;

    const clipboardData = e.clipboardData.getData('text');
    const rows = clipboardData.split(/\r?\n/).filter(row => row.trim());
    const pastedData = rows.map(row => {
      return row.split('\t').map(cell => cell.trim());
    });
    
    const totalRows = data.length;
    const selectedRows = tabulatorRef.current.getSelectedRows();
    
    // Get editable columns in correct order
    const editableColumns = [
      "STAGE_LEN",
      "NUMBER_CLUSTERS",
      "SAND_USAGE",
      "PRIMARY_FRACTURE_HALF_LEN",
      "PRIMARY_VERTICAL_PENETRATION",
      "PRIMARY_FRACTURE_WIDTH",
      "SECONDARY_FRACTURE_HALF_LEN",
      "SECONDARY_VERTICAL_PENETRATION",
      "SECONDARY_FRACTURE_WIDTH"
    ];

    // Validate row count and column count
    if (pastedData.length > totalRows) {
      alert(`Cannot paste ${pastedData.length} rows. Maximum allowed rows is ${totalRows}`);
      return;
    }

    // Require exactly 9 columns in all cases
    if (pastedData[0].length !== 9) {
      alert('Must paste exactly 9 columns');
      return;
    }

    // If rows are selected
    if (selectedRows.length > 0) {
      // Get all unselected rows
      const allRows = tabulatorRef.current.getRows();
      const unselectedRows = allRows.filter(row => !selectedRows.includes(row));
      
      // Case 1: pastedData.length <= selectedRows.length
      if (pastedData.length <= selectedRows.length) {
        pastedData.forEach((rowData, index) => {
          const updateData = {};
          rowData.forEach((value, colIndex) => {
            const field = editableColumns[colIndex];
            updateData[field] = isNumeric(value) ? parseFloat(value) : value;
          });
          selectedRows[index].update(updateData);
        });
      }
      // Case 2: pastedData.length > selectedRows.length
      else {
        // Update selected rows first
        selectedRows.forEach((row, index) => {
          const rowData = pastedData[index];
          const updateData = {};
          rowData.forEach((value, colIndex) => {
            const field = editableColumns[colIndex];
            updateData[field] = isNumeric(value) ? parseFloat(value) : value;
          });
          row.update(updateData);
        });

        // Then update unselected rows with remaining data
        const remainingData = pastedData.slice(selectedRows.length);
        remainingData.forEach((rowData, index) => {
          if (index < unselectedRows.length) {
            const updateData = {};
            rowData.forEach((value, colIndex) => {
              const field = editableColumns[colIndex];
              updateData[field] = isNumeric(value) ? parseFloat(value) : value;
            });
            unselectedRows[index].update(updateData);
          }
        });
      }
    }
    // If no rows are selected, paste starting from the first row
    else {
      const allRows = tabulatorRef.current.getRows();
      pastedData.forEach((rowData, index) => {
        const updateData = {};
        rowData.forEach((value, colIndex) => {
          const field = editableColumns[colIndex];
          updateData[field] = isNumeric(value) ? parseFloat(value) : value;
        });
        allRows[index].update(updateData);
      });
    }
  }, [data.length]);

  // Define handleCopy function
  const handleCopy = useCallback((e) => {
    if (e.ctrlKey && e.key === 'c' && tabulatorRef.current) {
      const selectedData = tabulatorRef.current.getSelectedData();
      if (selectedData.length) {
        const copyText = selectedData.map(row => 
          columns
            .filter(col => col.key !== 'operation' && col.key !== 'key')
            .map(col => row[col.dataIndex])
            .join('\t')
        ).join('\n');
        
        navigator.clipboard.writeText(copyText);
      }
    }
  }, [columns]);

  useEffect(() => {
    if (tableRef.current && !tabulatorRef.current) {
      tabulatorRef.current = new Tabulator(tableRef.current, {
        data: data,
        columns: [
          {
            formatter: "rowSelection", 
            titleFormatter: "rowSelection", 
            titleFormatterParams: {
              rowRange: "active"
            },
            hozAlign: "center",
            headerSort: false,
            width: 40,
            frozen: true
          },
          {
            title: "Stage\n#",
            field: "stageNumber",
            headerSort: false,
            width: 60,
            frozen: true,
            editor: false,
            headerHozAlign: "center",
            hozAlign: "center",
            formatter: function(cell) {
              return cell.getRow().getPosition(true);
            }
          },
          ...columns.map(col => ({
            ...col,
            title: col.title.replace(/\n/g, '<br/>'), // Preserve line breaks from parent
            editor: col.editable ? "input" : false,
            validator: col.editable ? ["required", "numeric"] : [],
            width: 120,
            headerHozAlign: "center",
            hozAlign: "right"
          }))
        ],
        layout: "fitData",
        height: "100%",
        selectable: true,
        selectableRangeMode: "click",
        clipboard: false, // Disable built-in clipboard handling
        keybindings: {
          "copy": false,
          "paste": false
        },
        dataChanged: function() {
          const updatedData = tabulatorRef.current.getData();
          setLocalData(updatedData);
          onDataChange(updatedData);
        }
      });

      // Custom paste handler
      const pasteFn = (e) => {
        e.stopPropagation();
        handlePaste(e);
      };

      // Add paste event listener to the table element directly
      tabulatorRef.current.element.addEventListener('paste', pasteFn);

      // Add copy event listener
      document.addEventListener('keydown', handleCopy);

      return () => {
        if (tabulatorRef.current) {
          tabulatorRef.current.element.removeEventListener('paste', pasteFn);
          document.removeEventListener('keydown', handleCopy);
          tabulatorRef.current.destroy();
          tabulatorRef.current = null;
        }
      };
    }
  }, [columns, handlePaste, handleCopy, data]);

  React.useImperativeHandle(ref, () => ({
    getCurrentData: () => {
      return tabulatorRef.current ? tabulatorRef.current.getData() : localData;
    }
  }));

  return (
    <div className="tabulator-container">
      <div ref={tableRef} />
    </div>
  );
});

export default TableEditor;